'use client';

import { useCallback, useEffect } from 'react';
import DatePicker from '../../../atoms/DatePicker/DatePicker';
import { getValidationResponse } from '../utils';
import { useFormContext } from '../FormContext';
import type { DatePickerField, DatePickerLocale } from '../types';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormGetValues,
  UseFormTrigger,
  FieldValues,
  FieldErrors,
} from 'react-hook-form';

export type DatePickerControlProps = {
  field: DatePickerField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  locale?: DatePickerLocale;
  /** Override display language for month/weekday labels (e.g. 'de', 'fr') */
  language?: string;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  errors: FieldErrors<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
};

/**
 * Form control for date picker fields
 * @component
 * @param {DatePickerControlProps} props - Component props
 * @returns {JSX.Element} DatePicker control component
 */
const DatePickerControl = ({
  field,
  theme,
  direction,
  locale,
  language,
  register,
  setValue,
  watch,
  getValues,
  errors,
  trigger,
}: DatePickerControlProps) => {
  const { disabled: formDisabled } = useFormContext();
  const isDisabled = field.disabled || formDisabled;
  const currentValue = watch(field.name);
  const dependencyName = field.dependency?.name;
  const dependencyValue = field.dependency?.value;
  const requiredMessage = field.validationMessages?.required;

  // Re-register when asynchronous labels update the validation message.
  useEffect(() => {
    register(field.name, {
      validate: (value) => {
        // Skip validation if dependency is not met
        if (dependencyName) {
          // Get current dependency value at validation time
          const depValue = getValues(dependencyName);
          // Handle array values (e.g., checkbox_group returns array of checked values)
          if (Array.isArray(depValue)) {
            if (!depValue.includes(dependencyValue)) {
              return true; // Dependency not met, skip required validation
            }
          } else if (depValue !== dependencyValue) {
            return true; // Dependency not met, skip required validation
          }
        }
        // Check required
        if (field.required && (!value || value === '')) {
          return requiredMessage || 'This field is required';
        }
        return true;
      },
    });
  }, [
    dependencyName,
    dependencyValue,
    field.name,
    field.required,
    getValues,
    register,
    requiredMessage,
  ]);

  const handleChange = useCallback(
    (date: Date) => {
      // Store as normalized date string (YYYY-MM-DD) to avoid timezone and reference issues
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      setValue(field.name, `${year}-${month}-${day}`, { shouldDirty: true, shouldValidate: true });
    },
    [setValue, field.name]
  );

  const handleClear = useCallback(() => {
    setValue(field.name, '', { shouldDirty: true, shouldValidate: true });
  }, [setValue, field.name]);

  return (
    <DatePicker
      key={`form_field_${field.name}`}
      theme={theme}
      size={field.size}
      name={field.name}
      locale={field.locale || locale}
      language={field.language || language}
      helperText={field.helperText}
      label={field.label}
      selectableDates={field.selectableDates}
      minDate={field.minDate}
      maxDate={field.maxDate}
      navigationMode={field.navigationMode}
      value={currentValue}
      onChange={handleChange}
      onClear={handleClear}
      onBlur={() => trigger(field.name)}
      required={field.required}
      disabled={isDisabled}
      validationResponse={getValidationResponse({ field: field.name, errors })}
      direction={direction}
    />
  );
};

export default DatePickerControl;
